/**
 * Building Info navigation config. Groups are sidebar children under Building
 * Info; each group's sections are the in-page tabs of
 * /b/:bId/buildingInfo/:group/:section (one section form per screen).
 * `idx` values are the legacy accordion indices (PROPERTY_TYPE / the old
 * /propertyAdd/:bId/:idx deep-link tokens); they drive which section a group
 * page renders and where numeric legacy links normalize to — keep them stable.
 */
export interface BuildingInfoSectionDef {
  slug: string
  label: string
  idx: number
  /** Permission flag gating the section; absent = visible to everyone. */
  permissionKey?: string
  /** Section only exists for Industrial buildings (Property Characteristics). */
  industrialOnly?: boolean
  /** Activate-internal only — hidden from customers however their roles are set. */
  acAdminOnly?: boolean
  /**
   * Suppressed from the nav — no tab in the Building Info strip and no hit in
   * the sidebar search — but the route, the section idx and direct URLs stay
   * live, so existing deep links keep resolving. Re-enable by deleting the flag.
   */
  hiddenFromNav?: boolean
}

export interface BuildingInfoGroup {
  slug: string
  label: string
  /**
   * Group is not a Building Info sidebar child — its sections were re-homed
   * elsewhere in the nav (Asset, COI, Data Archive, …) but still render
   * through the buildingInfo section shell, so idx deep links keep working.
   */
  hiddenFromNav?: boolean
  sections: BuildingInfoSectionDef[]
}

export const BUILDING_INFO_GROUPS: BuildingInfoGroup[] = [
  {
    slug: 'accounting',
    label: 'Accounting',
    // One PropertyMaterialCost page split into three tabs via its `part` prop;
    // idx 13 is the legacy whole-page deep-link token, 26/27 are new.
    sections: [
      { slug: 'accounting', label: 'General Info', idx: 13, permissionKey: 'MaterialAndCost' },
      { slug: 'glChargeCodes', label: 'GL & Charge Codes', idx: 26, permissionKey: 'MaterialAndCost' },
      { slug: 'materialsLabor', label: 'Materials & Labor', idx: 27, permissionKey: 'MaterialAndCost' },
    ],
  },
  {
    slug: 'branding',
    label: 'Branding & Content',
    sections: [
      { slug: 'branding', label: 'Branding', idx: 2, permissionKey: 'Branding' },
      { slug: 'content', label: 'Content', idx: 3, permissionKey: 'Content' },
      { slug: 'whiteLabel', label: 'White Label', idx: 12, permissionKey: 'WhiteLabel' },
      { slug: 'announcements', label: 'Announcements', idx: 17, permissionKey: 'Announcements' },
      { slug: 'documentLibrary', label: 'Document Library', idx: 18, permissionKey: 'DocumentLibrary' },
    ],
  },
  {
    slug: 'general',
    label: 'General',
    sections: [
      { slug: 'basicInfo', label: 'Basic Info', idx: 0, permissionKey: 'BasicInfo' },
      { slug: 'tags', label: 'Tags', idx: 20, permissionKey: 'BuildingPIN' },
      { slug: 'preferences', label: 'Preferences', idx: 21, permissionKey: 'BuildingPIN' },
    ],
  },
  {
    slug: 'operations',
    label: 'Operations',
    sections: [
      { slug: 'propertyCharacteristics', label: 'Property Characteristics', idx: 10, industrialOnly: true },
      {
        slug: 'managementResponsibility',
        label: 'Management Responsibility',
        idx: 16,
        permissionKey: 'ManagementResponsibility',
        // Suppressed from both navs (2026-09-16); route stays live.
        hiddenFromNav: true,
      },
      { slug: 'workOrders', label: 'Work Orders', idx: 19, permissionKey: 'WorkOrderSettings' },
      // Room Request (specs#3442). Was portfolio-wide and Activate-internal on
      // Account Info -> Analytics; moving its scope to the building must not
      // also widen who may set it, so it stays AcAdmin-only here.
      { slug: 'roomReservation', label: 'Room Reservation', idx: 28, acAdminOnly: true },
      // Suppressed from the nav (2026-09-16, no longer needed); route stays live.
      { slug: 'buildingPin', label: 'Building PIN', idx: 1, permissionKey: 'BuildingPIN', hiddenFromNav: true },
      { slug: 'surveyLinks', label: 'Customer Survey Links', idx: 24, permissionKey: 'CustomerSurveyLinks' },
    ],
  },
  // Re-homed sections: linked from their new nav homes, not from Building Info.
  {
    slug: 'asset',
    label: 'Asset',
    hiddenFromNav: true,
    sections: [{ slug: 'mappingHub', label: 'Mapping Hub', idx: 25, permissionKey: 'ManageMappingHub' }],
  },
  {
    slug: 'coi',
    label: 'COI',
    hiddenFromNav: true,
    sections: [{ slug: 'coi', label: 'COI Information', idx: 22, permissionKey: 'BuildingPIN' }],
  },
  {
    slug: 'dataArchive',
    label: 'Data Archive',
    hiddenFromNav: true,
    sections: [
      { slug: 'terms', label: 'Building Terms & Conditions', idx: 7, permissionKey: 'BuildingTermsAndConditions' },
    ],
  },
  // Visitor Badge Sizes (idx 23) renders as a tab on the Visitor & Security
  // page (VisitorBadgeSizesTab), not through this shell.
  {
    slug: 'activate',
    label: 'Activate',
    hiddenFromNav: true,
    sections: [
      { slug: 'productAddOns', label: 'Product Add-Ons', idx: 5, permissionKey: 'ProductAddOns' },
      { slug: 'upsells', label: 'Upsells', idx: 9, permissionKey: 'Upsells' },
    ],
  },
  // Suppressed (2026-08-22 nav reorg), kept for a future senior tier:
  // { slug: 'residentIndex', label: 'Resident Index', idx: 11, permissionKey: 'ResidentIndex' }
]

/**
 * Group is visible when the user holds at least one of its sections'
 * permission flags. An acAdminOnly section counts only for an AcAdmin — it
 * carries no permissionKey, so without the flag it can never open the group
 * for a customer.
 */
export const isGroupVisible = (
  group: BuildingInfoGroup,
  permissions: Record<string, any> | undefined,
  isAcAdmin?: boolean
): boolean =>
  group.sections.some(
    (section) =>
      !section.hiddenFromNav &&
      (section.acAdminOnly
        ? !!isAcAdmin
        : !!section.permissionKey && !!permissions?.[section.permissionKey])
  )

export const isSectionVisible = (
  section: BuildingInfoSectionDef,
  permissions: Record<string, any> | undefined,
  isIndustrial: boolean,
  isAcAdmin?: boolean
): boolean =>
  !section.hiddenFromNav &&
  (!section.acAdminOnly || !!isAcAdmin) &&
  (section.permissionKey ? !!permissions?.[section.permissionKey] : true) &&
  (!section.industrialOnly || isIndustrial)

/** Owning group + section slugs for a legacy accordion idx (falls back to General/Basic Info). */
export const sectionOfIdx = (idx: number): { group: string; section: string } => {
  for (const group of BUILDING_INFO_GROUPS) {
    const section = group.sections.find((s) => s.idx === idx)
    if (section) return { group: group.slug, section: section.slug }
  }
  return { group: BUILDING_INFO_GROUPS[0].slug, section: BUILDING_INFO_GROUPS[0].sections[0].slug }
}

/**
 * Owning group for a section slug (slugs are unique across groups). Lets old
 * bookmarks like /buildingInfo/access/coi keep resolving after the nav reorg
 * moved sections between groups.
 */
export const sectionOfSlug = (slug: string): { group: string; section: string } | undefined => {
  for (const group of BUILDING_INFO_GROUPS) {
    const section = group.sections.find((s) => s.slug === slug)
    if (section) return { group: group.slug, section: section.slug }
  }
  return undefined
}
